// src/pages/admin/adminpanel/ModerationPanel.jsx
import React, { useState, useRef, useEffect } from 'react';

const STATUS_FILTERS = ['pending', 'active', 'inactive'];

const statusStyle = (s) => ({
  pending: 'bg-amber-100 text-amber-700',
  active:  'bg-emerald-100 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',  
}[s] || 'bg-gray-100 text-gray-600');

function usePagination(data, perPage) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) pages.push(i);
    else if (pages[pages.length - 1] !== '...') pages.push('...');
  }
  return (
    <div className="flex items-center justify-between mt-4 px-1">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>Prev
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => p === '...' ? (
          <span key={`d-${i}`} className="px-1 text-xs text-secondary">…</span>
        ) : (
          <button key={p} onClick={() => setPage(p)}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
              ${page === p ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'}`}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        Next<span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

export default function ModerationPanel({ animals, butcherProducts = [], auctions = [], loading, onAction, onProductAction, onAuctionApproval, onRequestChanges, highlightAnimal, highlightProduct }) {
  const [tab, setTab] = useState(() => {
    const saved = sessionStorage.getItem('modTab');
    return ['animals', 'products', 'auctions'].includes(saved) ? saved : 'animals';
  });
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [toast,  setToast]  = useState(null); // { msg, type }
  const [changeRequest, setChangeRequest] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const SOURCE = tab === 'animals' ? animals : tab === 'products' ? butcherProducts : [];
  const nameKey = tab === 'animals' ? 'seller_name' : 'butcher_name';
  const ACTION  = tab === 'animals' ? onAction : onProductAction;
  const isAuctions = tab === 'auctions';
  const REJECT_STATUS = tab === 'animals' ? 'inactive' : 'inactive';

  const filtered = SOURCE
    .filter(a => a.status === filter)
    .filter(a =>
      !search ||
      a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a[nameKey]?.toLowerCase().includes(search.toLowerCase())
    );
  
    const [perPage, setPerPage] = useState(5);
    const pagination = usePagination(filtered, perPage);
    const highlightRef = useRef(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { pagination.reset(); }, [filter, tab, search]);

    useEffect(() => {
      if (highlightRef.current) {
        highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, [highlightAnimal, highlightProduct]);

    useEffect(() => {
      if (highlightProduct) {
        setTab('products');
        setFilter('pending');
      }
    }, [highlightProduct]);

  const handleAction = async (id, status) => {
    await ACTION(id, status);  
    const label = status === 'active' ? 'Approved' : 'Rejected';
    setToast({ msg: `Listing ${label} successfully`, type: status });
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <div className="space-y-5">

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5
                         rounded-2xl shadow-2xl border text-white animate-fade-in max-w-xs
                         ${toast.type === 'active'
                           ? 'bg-emerald-800 border-emerald-600'
                           : 'bg-red-800 border-red-600'}`}>
          <span className="material-symbols-outlined text-[20px]">
            {toast.type === 'active' ? 'check_circle' : 'cancel'}
          </span>
          <p className="text-xs font-bold">{toast.msg}</p>
        </div>
      )}

      {/* Header row */}
      <div className="flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-3">
        {/* Tab switcher */}
        <div className="flex gap-2 p-1 bg-surface-container rounded-xl w-fit">
          {[
            { key: 'animals',  label: '🐐 Animals',       count: animals.filter(a => a.status === 'pending').length },
            { key: 'products', label: '🥩 Meat Products',  count: butcherProducts.filter(p => p.status === 'pending').length },
            { key: 'auctions', label: '🔨 Auctions',       count: auctions.filter(a => a.approval_status === 'pending_approval').length },
          ].map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setFilter('pending'); sessionStorage.setItem('modTab', t.key); }}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                tab === t.key
                  ? 'bg-white shadow text-primary'
                  : 'text-secondary hover:text-primary'
              }`}>
              {t.label}
              {t.count > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none">
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Filter pills */}
        {!isAuctions && <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map((s) => {
            const count = SOURCE.filter(a => a.status === s).length;
            return (
              <button key={s} onClick={() => setFilter(s)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all
                            ${filter === s
                              ? 'bg-primary text-white shadow-sm'
                              : `${statusStyle(s)} hover:opacity-80`}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
                <span className="ml-1 opacity-70">({count})</span>
              </button>
            );
          })}
        </div>}

        {/* Search */}
        <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl
                        border border-outline-variant/20 shadow-sm w-full md:w-auto">
          <span className="material-symbols-outlined text-outline text-sm">search</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search animal or seller…"
            className="bg-transparent outline-none text-sm w-48
                       placeholder:text-outline-variant"
          />
          {search && (
            <button onClick={() => setSearch('')}
              className="text-outline hover:text-on-surface transition-colors">
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary
                          border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && !isAuctions && (
        <div className="bg-white rounded-2xl p-14 text-center shadow-sm
                        border border-outline-variant/10">
          <span className="material-symbols-outlined text-5xl text-emerald-400">
            {filter === 'pending' ? 'check_circle' : 'search_off'}
          </span>
          <p className="mt-3 font-bold text-on-surface text-lg">
            {filter === 'pending'
              ? 'All clear! No pending listings.'
              : `No ${filter} listings found.`}
          </p>
          {search && (
            <p className="text-sm text-secondary mt-1">Try a different search term</p>
          )}
        </div>
      )}

      {/* Cards */}
      {!loading && pagination.slice.map((item) => (
        <div key={item.id}
          ref={
              (highlightAnimal && item.name === highlightAnimal) ||
              (highlightProduct && item.name === highlightProduct)
                ? highlightRef : null
            }
            className={`bg-white rounded-2xl p-4 shadow-sm border flex flex-col sm:flex-row items-start sm:items-center gap-4 transition-all
              ${(highlightAnimal && item.name === highlightAnimal) ||
                (highlightProduct && item.name === highlightProduct)
                  ? 'border-primary ring-2 ring-primary/30 bg-primary/5'
                  : 'border-outline-variant/10 hover:border-primary/20'
              }`}>
          {/* Image */}
          {item.image_url
            ? <img src={item.image_url} alt={item.name}
                   className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
            : <div className="w-20 h-20 rounded-xl bg-surface-container
                              flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-outline text-3xl">pets</span>
              </div>
          }

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-on-surface">{item.name}</p>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold
                                ${statusStyle(item.status)}`}>
                {item.status}
              </span>
            </div>
            <p className="text-sm text-secondary mt-0.5">
              {item.category}
              {item.breed ? ` · ${item.breed}` : ''}
              {' · '}
              <span className="font-bold text-primary">
                ₹{Number(item.price_inr).toLocaleString('en-IN')}
              </span>
            </p>
            <p className="text-xs text-secondary mt-0.5">
              Seller: <strong className="text-on-surface">{item[nameKey]}</strong>
              {item.location_city && ` · ${item.location_city}`}
              {item.location_state && `, ${item.location_state}`}
            </p>
            {item.description && (
              <p className="text-xs text-outline mt-1.5 line-clamp-1">
                {item.description}
              </p>
            )}
            <p className="text-[10px] text-outline mt-1 font-mono">
              ID #{item.id} · Submitted {item.created_at
                ? new Date(item.created_at).toLocaleDateString('en-IN')
                : '—'}
            </p>
            <button onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
              className="text-[11px] font-bold text-primary hover:underline mt-1">
              {expandedId === item.id ? 'Hide Details' : 'View Full Details'}
            </button>

            {expandedId === item.id && (
            <div className="mt-3 pt-3 border-t border-outline-variant/20 grid grid-cols-2 sm:grid-cols-3 gap-2 w-full">
              {tab === 'animals' ? (
                <>
                  {[
                    { label: 'Age',         value: item.age_months  ? `${item.age_months} months` : '—' },
                    { label: 'Weight',      value: item.weight_kg   ? `${item.weight_kg} kg`      : '—' },
                    { label: 'Gender',      value: item.gender      || '—' },
                    { label: 'Teeth Count', value: item.teeth_count ?? '—' },
                    { label: 'Health',      value: item.health_status || '—' },
                    { label: 'Quantity',    value: item.quantity    ?? '—' },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-surface-container rounded-lg px-3 py-2">
                      <p className="text-[9px] font-bold uppercase tracking-widest text-outline">{label}</p>
                      <p className="text-xs font-bold text-on-surface mt-0.5">{value}</p>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {[
                    { label: 'Weight',  value: item.weight_kg  ? `${item.weight_kg} kg` : '—' },
                    { label: 'Unit',    value: item.unit        || '—' },
                    { label: 'Stock',   value: item.stock_qty   ?? '—' },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-surface-container rounded-lg px-3 py-2">
                      <p className="text-[9px] font-bold uppercase tracking-widest text-outline">{label}</p>
                      <p className="text-xs font-bold text-on-surface mt-0.5">{value}</p>
                    </div>
                  ))}
                </>
              )}
              {item.description && (
                <div className="col-span-2 sm:col-span-3 bg-surface-container rounded-lg px-3 py-2">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-outline">Description</p>
                  <p className="text-xs text-on-surface mt-0.5">{item.description}</p>
                </div>
              )}
            </div>
          )}
          </div>

          {/* Actions */}
          <div className="flex flex-row sm:flex-col gap-2 flex-shrink-0 w-full sm:w-auto">
            {item.status !== 'active' && (
              <button onClick={() => handleAction(item.id, 'active')}
                className="px-5 py-2 rounded-full text-xs font-bold
                           bg-emerald-100 text-emerald-700
                           hover:bg-emerald-600 hover:text-white transition-all
                           flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">check</span>
                Approve
              </button>
            )}
            {item.status !== REJECT_STATUS && (
              <button onClick={() => handleAction(item.id, REJECT_STATUS)}
                className="px-5 py-2 rounded-full text-xs font-bold
                           bg-red-100 text-red-600
                           hover:bg-red-600 hover:text-white transition-all
                           flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">close</span>
                Reject
              </button>
            )}
            {item.status !== 'sold' && (
              <button onClick={() => setChangeRequest({ id: item.id, name: item.name, tab })}
                className="px-5 py-2 rounded-full text-xs font-bold bg-amber-100 text-amber-700 hover:bg-amber-600 hover:text-white transition-all flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">edit_note</span>
                Request Changes
              </button>
            )}
          </div>

        </div>
      ))}
      {/* Auction approval cards */}
      {!loading && isAuctions && auctions.map(auction => (
        <div key={auction.id}
          className="bg-white rounded-2xl p-4 shadow-sm border border-outline-variant/10 flex flex-col sm:flex-row items-start gap-4 hover:border-primary/20 transition-all">
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-surface-container flex-shrink-0">
            {auction.image_url
              ? <img src={auction.image_url} alt={auction.animal_name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-outline text-3xl">gavel</span>
                </div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-on-surface">{auction.title || auction.animal_name}</p>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                auction.approval_status === 'approved'  ? 'bg-emerald-100 text-emerald-700' :
                auction.approval_status === 'rejected'  ? 'bg-red-100 text-red-600' :
                                                          'bg-blue-100 text-blue-600'
              }`}>
                {auction.approval_status === 'approved' ? '✓ Approved' :
                auction.approval_status === 'rejected' ? '✗ Rejected' : '⏳ Pending'}
              </span>
            </div>
            <p className="text-sm text-secondary mt-0.5">
              {auction.animal_name} · {auction.breed} · {auction.weight_kg}kg
            </p>
            <p className="text-xs text-secondary mt-0.5">
              Seller: <strong>{auction.seller_name}</strong>
            </p>
            <p className="text-xs text-secondary mt-0.5">
              Start Price: <strong className="text-primary">₹{Number(auction.start_price).toLocaleString('en-IN')}</strong>
              {auction.base_price && auction.start_price < auction.base_price && (
                <span className="ml-2 text-amber-600">
                  (Base: ₹{Number(auction.base_price).toLocaleString('en-IN')} · {auction.discount_type === 'percent' ? `${auction.discount_value}% off` : `₹${auction.discount_value} off`})
                </span>
              )}
            </p>
            <p className="text-[10px] text-outline mt-1">
              {new Date(auction.start_time).toLocaleString('en-IN')} → {new Date(auction.end_time).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="flex flex-row sm:flex-col gap-2 flex-shrink-0">
            {auction.approval_status !== 'approved' && (
              <button onClick={() => onAuctionApproval(auction.id, 'approved')}
                className="px-5 py-2 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-all flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">check</span>Approve
              </button>
            )}
            {auction.approval_status !== 'rejected' && (
              <button onClick={() => onAuctionApproval(auction.id, 'rejected')}
                className="px-5 py-2 rounded-full text-xs font-bold bg-red-100 text-red-600 hover:bg-red-600 hover:text-white transition-all flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">close</span>Reject
              </button>
            )}
          </div>
        </div>
      ))}

      {!loading && isAuctions && auctions.length === 0 && (
        <div className="bg-white rounded-2xl p-14 text-center shadow-sm border border-outline-variant/10">
          <span className="material-symbols-outlined text-5xl text-emerald-400">gavel</span>
          <p className="mt-3 font-bold text-on-surface text-lg">No auctions to review</p>
        </div>
      )}
          
      {!loading && filtered.length > 0 && (
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {[5, 10, 20].map(n => (
            <button key={n} onClick={() => { setPerPage(n); pagination.reset(); }}
              className={`w-8 h-7 rounded-lg text-xs font-bold transition-all
                ${perPage === n ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container border border-surface-container-high'}`}>
              {n}
            </button>
          ))}
        </div>
        <p className="text-xs text-secondary">
          {((pagination.page-1)*perPage)+1}–{Math.min(pagination.page*perPage, filtered.length)} of {filtered.length}
        </p>
      </div>
    )}
    {!loading && <PaginationBar page={pagination.page} totalPages={pagination.totalPages} setPage={pagination.setPage} />}

    {/* Request Changes Modal */}
    {changeRequest && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-md mx-4 space-y-4">
          <h3 className="font-bold text-on-surface">Request Changes</h3>
          <p className="text-xs text-secondary">For: <strong>{changeRequest.name}</strong></p>
          <textarea
            id="cr-msg"
            rows={3}
            placeholder="Describe what needs to be changed..."
            className="w-full border border-outline-variant/30 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-primary resize-none"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setChangeRequest(null)}
              className="px-4 py-2 rounded-full text-xs font-bold text-secondary hover:bg-surface-container transition-all">
              Cancel
            </button>
            <button onClick={() => {
              const msg = document.getElementById('cr-msg').value;
              if (msg.trim()) { onRequestChanges(changeRequest.id, msg, changeRequest.tab); setChangeRequest(null); }
            }}
              className="px-4 py-2 rounded-full text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 transition-all">
              Send Request
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}